import { Typography, Dialog, DialogContent, DialogContentText, DialogActions, Button, Box, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, useMediaQuery, useTheme } from '@mui/material';
import { TFunction, useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { equivalencyRequestNavigation } from '../../application/App.routes';
import EquivalencyRequest, { EquivalencyRequestStates, Tag } from '../../models/EquivalencyRequest';
import './PreviousRequestModal.css';
import { formatDate } from '../../shared/utils/formatDate';
import React, {useEffect, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useRequestType } from '../../shared/hooks/useRequestType';

interface PreviousRequestModalProps {
  pendingRequests: EquivalencyRequest[];
  endedRequests: EquivalencyRequest[];
  isOpen: boolean;
  onClose: (isContinue: boolean) => void;
  tags: Tag[]
  equivalencyRuleCheckEnabled: boolean
};

interface RenderDefaultTextProps {
  t: TFunction;
  allRequests: (EquivalencyRequest & { status: string })[];
  endedRequests: EquivalencyRequest[];
  checkMatchingActivities: (requests: EquivalencyRequest[]) => boolean;
  isInternal: boolean;
}

const RenderDefaultText: React.FC<RenderDefaultTextProps> = ({t, allRequests, endedRequests, checkMatchingActivities, isInternal}) => (  <>
    <Typography marginBottom="10px">
      {isInternal ? t('previousRequestText2') : t('previousRequestText1')}
      {checkMatchingActivities(allRequests) && t('previousRequestTextWithActivities')}
      {isInternal ? t('previousRequestText4') : t('previousRequestText3')}
    </Typography>
    {endedRequests.length > 0 && (
      <Typography marginBottom="10px">
        {t('previousRequestEndedText')}
      </Typography>
    )}
  </>
);
const PreviousRequestModal = ({ isOpen, pendingRequests, onClose, endedRequests, tags, equivalencyRuleCheckEnabled}: PreviousRequestModalProps) => {
  const { t } = useTranslation();
  const { isInternal } = useRequestType();
  const handleClosePreviousRequestModal = (_event: React.SyntheticEvent<Element, Event>, reason: string) => {
    if (reason && (reason === 'backdropClick' || reason === 'escapeKeyDown'))
      return;
    onClose(false);
  };
  const [expandedIds, setExpandedIds] = useState({} as Record<string, boolean>);
  const toggleExpanded = (id: string) => {
    setExpandedIds(prev => ({ ...prev, [id]: !prev[id] }));
  };
  const [nameTagMatched, setNameTagMatched] = useState<string | null>(null)
  const [hasSpecialTag, setHasSpecialTag] = useState<boolean>(false)

  const allRequests = [
    ...pendingRequests.map((req) => ({ ...req, status: t('pendingLabel') })),
    ...endedRequests.map((req) => ({ ...req, status: isInternal ? t('endedLabel') + (' (')+ (req.state === EquivalencyRequestStates.aproved? t('approvedEquivalencyRequestState') : t('rejectedEquivalencyRequestTitle')+(')')) : t('endedLabel') }))
  ];


  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const checkMatchingActivities = (requests: EquivalencyRequest[]) => {
    if(requests.some((req) => req.matchingActivities && req.matchingActivities.length > 0))
      return true;
    return false;
  }

useEffect(() => {
  const keywords = ['Segunda carrera', 'Articulación', 'Doble titulación'];
  if(tags){
    const matchingTag = tags?.find((item: any) =>
      keywords.some(tags => item.tag.toLowerCase() === tags.toLowerCase())
    );
    if (matchingTag) {
    setHasSpecialTag(true)
    setNameTagMatched(matchingTag.tag);
  }
  }
}, [tags]);

let descriptionContent;

if (!equivalencyRuleCheckEnabled || !hasSpecialTag) {
  descriptionContent = (
    <RenderDefaultText
      t={t}
      allRequests={allRequests}
      endedRequests={endedRequests}
      checkMatchingActivities={checkMatchingActivities}
      isInternal={isInternal}  
    />
  );
} else {
  descriptionContent = (
    <>
      {pendingRequests.length > 0 && (
        <Typography marginBottom="10px">
          {t('pendingRequestOne')} <b>{nameTagMatched}</b> {t('pendingRequestTwo')}{' '}
          <b>{allRequests[0].syllabus?.name}</b> {t('pendingRequestThree')}{' '}
          <b>{allRequests[0].target_syllabus?.name}</b>. {t('pendingRequestFour')}{' '}
          <NavLink
            to={equivalencyRequestNavigation.searchEquivalency(
              `?documentType=${allRequests[0].student.document_type}&documentNumber=${allRequests[0].student.document_number}&caseNumber=${allRequests[0].case_number}`
            )}
            target="_blank"
            style={{ textDecoration: 'underline', color: '#479184', cursor: 'pointer' }}
          >
            {allRequests[0].case_number}
          </NavLink>{' '}
          {t('pendingRequestFive')}
        </Typography>
      )}

      {endedRequests.length > 0 && (
        <Typography marginBottom="10px">
          {t('endedRequestOne')} <b>{nameTagMatched}</b> {t('endedRequestTwo')}{' '}
          <b>{allRequests[0].syllabus?.name}</b> {t('endedRequestThree')}{' '}
          <b>{allRequests[0].target_syllabus?.name}</b> {t('endedRequestFour')}{' '}
          <NavLink
            to={equivalencyRequestNavigation.searchEquivalency(
              `?documentType=${allRequests[0].student.document_type}&documentNumber=${allRequests[0].student.document_number}&caseNumber=${allRequests[0].case_number}`
            )}
            target="_blank"
            style={{ textDecoration: 'underline', color: '#479184', cursor: 'pointer' }}
          >
            {allRequests[0].case_number}
          </NavLink>{' '}
          {t('endedRequestFive')}
        </Typography>
      )}
    </>
  );
}

  return (
    <Dialog
      open={isOpen}
      onClose={handleClosePreviousRequestModal}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      fullWidth
      maxWidth='md'
      scroll='paper'
      fullScreen={isMobile}
    >
      <DialogContent>
        <DialogContentText id='alert-dialog-description' style={{ display: 'flex', flexDirection: 'column', gap: 6, color: 'black' }}>
          <Typography fontSize='20px' marginBottom={'14px'} fontWeight='bold'>{pendingRequests.length > 0 ? t('previousRequestTitle') : t('previousRequestEndedTitle')}</Typography>

          {descriptionContent}

          <TableContainer className="REQXXI-Previus-TableBody">
            <Table stickyHeader size="small" sx={{ minWidth: 500 }} aria-label="customized table">
              <TableHead>
                <TableRow>
                  <TableCell className="REQXXI-Previus-TableItem" align="left" scope="row">
                    <Typography> {t('caseNumberLabel')}</Typography>
                  </TableCell>
                  <TableCell className="REQXXI-Previus-TableItem" align="left">
                    <Typography>{t('statusLabel')}</Typography>
                  </TableCell>
                  <TableCell className="REQXXI-Previus-TableItem" align="left">
                    <Typography>{t('createdDate')}</Typography>
                  </TableCell>
                  <TableCell className="REQXXI-Previus-TableItem" align="left">
                    <Typography>{t('associatedSubjects')}</Typography>
                  </TableCell>
                  <TableCell className="REQXXI-Previus-TableItem" align="left">
                    <Typography>{t('linkLabel')}</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
              {allRequests.map((request) => (
                <React.Fragment key={request.id}>
                  <TableRow sx={{ width: '100%' }}>
                    <TableCell>{request.case_number}</TableCell>
                    <TableCell>{request.status}</TableCell>
                    <TableCell>{formatDate(request.created_date)}</TableCell>
                    <TableCell>
                      {request.matchingActivities && request.matchingActivities.length > 0 ? (
                        <Box
                          onClick={() => toggleExpanded(request.id)}
                          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          {expandedIds[request.id] ? t('hideSubjects') : t('showSubjects')}
                          {expandedIds[request.id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </Box>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <NavLink
                        to={equivalencyRequestNavigation.searchEquivalency(`?documentType=${request.student.document_type}&documentNumber=${request.student.document_number}&caseNumber=${request.case_number}`)}
                        target="_blank"
                        style={{ textDecoration: 'underline', color: '#479184', cursor: 'pointer' }}
                      >
                        {t('viewLabelPending')}
                      </NavLink>
                    </TableCell>
                  </TableRow>
                  {expandedIds[request.id] && request.matchingActivities && request.matchingActivities.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Box sx={{ pt: 1, pb: 1 }}>
                          {request.matchingActivities.map((activity) => (
                            <Typography key={activity.id} sx={{ fontSize: '13px' }}>
                              {activity.name}{activity.code ? ` - ${activity.code}` : ''}
                            </Typography>
                          ))}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
              </TableBody>
            </Table>
          </TableContainer >
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ paddingRight: '20px', paddingBottom: '20px' }}>
        <Button onClick={() => onClose(false)} color='primary'>
          {t('backLabel')}
        </Button>
        {<Button 
          disabled={checkMatchingActivities(allRequests)}
          onClick={() => {
            onClose(true)
            }} color='primary' variant='contained'>
            {t('continueLabel')}
        </Button>}
      </DialogActions>
    </Dialog>
  );
};

export default PreviousRequestModal;
